import OpenAI, {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { supabase } from '../lib/supabase';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const aiLetterGenerationService = {
  /**
   * Generate a letter using AI based on employee data and letter purpose
   */
  async generateLetter(employeeId, letterPurpose, additionalDetails = {}) {
    try {
      // 1. Get employee data
      const { data: employee, error: empError } = await supabase
        ?.from('employees')
        ?.select('*')
        ?.eq('id', employeeId)
        ?.single();

      if (empError) throw empError;
      if (!employee) throw new Error('Employee not found');

      // 2. Get company settings
      const { data: companySettings, error: companyError } = await supabase
        ?.from('company_settings')
        ?.select('*')
        ?.limit(1)
        ?.single();

      if (companyError) console.log('Company settings not found, using defaults');

      // 3. Get letter template
      const { data: template, error: templateError } = await supabase
        ?.from('letter_templates')
        ?.select('*')
        ?.eq('letter_purpose', letterPurpose)
        ?.eq('is_active', true)
        ?.single();

      if (templateError) throw new Error(`Letter template not found for purpose: ${letterPurpose}`);

      // 4. Build context for AI
      const companyName = companySettings?.company_name || 'Our Organization';
      const employeeFullName = `${employee?.first_name} ${employee?.last_name}`;
      const employmentType = employee?.employment_type || 'Full-time';
      const hireDate = employee?.hire_date ? new Date(employee?.hire_date)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A';
      const position = employee?.position || 'Staff Member';
      const department = employee?.department || 'General';
      const employmentStatus = employee?.is_active ? 'Active Employment' : 'Former Employee';

      // 5. Build AI prompt
      const systemPrompt = `You are a professional HR letter writer for ${companyName}, a company in Papua New Guinea. Generate formal, professional employment letters that comply with PNG business standards and regulations. Use proper business letter format with appropriate salutations and closings.`;

      const userPrompt = `${template?.ai_prompt_template}

Employee Details:
- Full Name: ${employeeFullName}
- Position: ${position}
- Department: ${department}
- Employment Type: ${employmentType}
- Hire Date: ${hireDate}
- Employment Status: ${employmentStatus}
- Company: ${companyName}

Additional Details: ${JSON.stringify(additionalDetails)}

Generate a complete, professional letter ready for HR manager review and signature. Use [HR Manager Name] and [HR Manager Title] as placeholders for the signature block.`;

      // 6. Call OpenAI API
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        reasoning_effort: 'low',
        verbosity: 'medium',
      });

      const generatedContent = response?.choices?.[0]?.message?.content;

      if (!generatedContent) {
        throw new Error('Failed to generate letter content');
      }

      return {
        success: true,
        content: generatedContent,
        metadata: {
          model: 'gpt-5-mini',
          employeeName: employeeFullName,
          letterPurpose,
          generatedAt: new Date()?.toISOString(),
        },
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating letter:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Regenerate letter with modifications
   */
  async regenerateLetter(letterRequestId, modifications) {
    try {
      // Get existing letter request
      const { data: letterRequest, error: requestError } = await supabase
        ?.from('letter_requests')
        ?.select('*, employees(*)')
        ?.eq('id', letterRequestId)
        ?.single();

      if (requestError) throw requestError;
      if (!letterRequest) throw new Error('Letter request not found');

      // Generate new content with modifications
      const systemPrompt = 'You are a professional HR letter writer. Modify the existing letter based on the requested changes while maintaining professional format and tone.';

      const userPrompt = `Original Letter:
${letterRequest?.generated_content}

Requested Modifications:
${modifications}

Generate the updated letter incorporating these changes.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        reasoning_effort: 'low',
        verbosity: 'medium',
      });

      const regeneratedContent = response?.choices?.[0]?.message?.content;

      if (!regeneratedContent) {
        throw new Error('Failed to regenerate letter content');
      }

      // Update letter request
      const { data: updated, error: updateError } = await supabase
        ?.from('letter_requests')
        ?.update({
          generated_content: regeneratedContent,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', letterRequestId)
        ?.select()
        ?.single();

      if (updateError) throw updateError;

      return {
        success: true,
        content: regeneratedContent,
        letterRequestId: updated?.id,
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error regenerating letter:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },
};

export default aiLetterGenerationService;